import React, { useState, useRef, useEffect, useCallback } from "react";
import { ArrowUp, Loader2, Paperclip, Plus, Rocket } from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";
import VoiceInputButton from "./VoiceInputButton";
import InstallNudge from "./InstallNudge";
import VoiceReplyToggle from "./VoiceReplyToggle";
import SlashSkillMenu, { slashQuery } from "./Skills/SlashSkillMenu";
import { useFeatureFlag, FLAGS } from "../config/featureFlags";
import "./ChatInterface.css";

/**
 * Supported media types for drag-and-drop upload
 * Claude API supports these natively as base64
 */
const SUPPORTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];
const SUPPORTED_PDF_TYPES = ["application/pdf"];
// Text-family files (CSV first — Melissa call item #8: "you can't upload
// CSVs") are read client-side and folded into the message as fenced text —
// they ride the normal text path, no special API blocks needed.
const SUPPORTED_TEXT_TYPES = [
  "text/csv",
  "text/plain",
  "text/markdown",
  "text/tab-separated-values",
  "application/json",
  "application/vnd.ms-excel", // what Windows/Excel stamps on .csv files
];
const TEXT_FILE_EXTENSIONS = [".csv", ".tsv", ".txt", ".md", ".json"];
const isTextFile = (file) =>
  SUPPORTED_TEXT_TYPES.includes(file.type) ||
  TEXT_FILE_EXTENSIONS.some((ext) => (file.name || "").toLowerCase().endsWith(ext));
// Audio/video (sales-call recordings etc.) can't ride the chat as base64 —
// they route to the Files upload endpoint, which auto-transcribes via
// Deepgram to "<name>.transcript.txt" in the user's MinIO workspace. The
// no-homework coaching loop depends on this path being real (Brad 2026-09-01).
const AV_EXTENSIONS = [".mp3", ".m4a", ".wav", ".aac", ".ogg", ".flac", ".mp4", ".mov", ".webm", ".mkv", ".avi"];
const isAudioVideoFile = (file) =>
  (file.type || "").startsWith("audio/") ||
  (file.type || "").startsWith("video/") ||
  AV_EXTENSIONS.some((ext) => (file.name || "").toLowerCase().endsWith(ext));
const MAX_AV_FILE_MB = 500; // matches /api/c3/files/upload multer limit
const MAX_TEXT_FILE_BYTES = 400 * 1024; // keep prompts sane
const ALL_SUPPORTED_TYPES = [...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_PDF_TYPES];
// Images and PDFs travel inline as base64 inside the chat JSON body, and the
// server's JSON limit is 10 MB (server/app.js express.json). A 20 MB cap here
// let a 15 MB PDF pass the picker and die as a 413 at the server (Ashley,
// 2026-09-08). 6 MB raw ≈ 8 MB base64 leaves room for the message itself.
const MAX_FILE_SIZE_MB = 6;

/**
 * Convert a File to base64 data URL
 * @param {File} file - The file to convert
 * @returns {Promise<{type: string, mediaType: string, data: string, name: string, size: number}>}
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result is "data:image/png;base64,iVBOR..."
      const dataUrl = reader.result;
      const base64Data = dataUrl.split(",")[1]; // Remove the data:mime;base64, prefix
      const mediaType = file.type;
      const isImage = SUPPORTED_IMAGE_TYPES.includes(mediaType);

      resolve({
        type: isImage ? "image" : "document",
        mediaType,
        data: base64Data,
        name: file.name,
        size: file.size,
        previewUrl: isImage ? dataUrl : null, // Only images get previews
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Format tool input/result for human-readable display
 * Works generically for ANY MCP tool (6000+ tools across 260+ servers)
 *
 * Philosophy: Be dumb. Show what's there. Truncate for length, don't filter for "usefulness".
 * Let the human decide what matters.
 */
const formatToolDataHuman = (data, maxLines = 20) => {
  if (data === null || data === undefined) return "No data";

  // Handle strings
  if (typeof data === "string") {
    // Try to parse as JSON
    try {
      const parsed = JSON.parse(data);
      return formatToolDataHuman(parsed, maxLines);
    } catch {
      // Plain string - just show it truncated
      return truncateStr(data, 500);
    }
  }

  // Handle primitives
  if (typeof data === "boolean") return data ? "true" : "false";
  if (typeof data === "number") return String(data);

  // Handle arrays
  if (Array.isArray(data)) {
    return formatArray(data, maxLines);
  }

  // Handle objects
  if (typeof data === "object") {
    return formatObject(data, maxLines);
  }

  return String(data).substring(0, 500);
};

/**
 * Truncate string with ellipsis
 */
const truncateStr = (str, max) => {
  if (!str || typeof str !== "string") return String(str || "");
  return str.length > max ? str.substring(0, max) + "..." : str;
};

/**
 * Format array - show count and first few items
 */
const formatArray = (arr, maxLines) => {
  if (arr.length === 0) return "Empty array (0 items)";

  const lines = [];
  lines.push(`Array with ${arr.length} item${arr.length !== 1 ? "s" : ""}:`);

  // Show first few items
  const showCount = Math.min(arr.length, 5);
  for (let i = 0; i < showCount; i++) {
    const item = arr[i];
    const prefix = `  ${i + 1}. `;

    if (item === null || item === undefined) {
      lines.push(prefix + "(empty)");
    } else if (typeof item === "string") {
      lines.push(prefix + truncateStr(item, 80));
    } else if (typeof item === "object") {
      // Get a quick summary of the object
      lines.push(prefix + getObjectSummary(item));
    } else {
      lines.push(prefix + String(item));
    }
  }

  if (arr.length > 5) {
    lines.push(`  ... +${arr.length - 5} more`);
  }

  return lines.slice(0, maxLines).join("\n");
};

/**
 * Get a one-line summary of an object
 * Looks for identifying fields, falls back to showing first few keys
 */
const getObjectSummary = (obj) => {
  if (!obj || typeof obj !== "object") return String(obj);
  if (Array.isArray(obj)) return `[${obj.length} items]`;

  const keys = Object.keys(obj);
  if (keys.length === 0) return "{}";

  // Priority fields to look for (things humans care about)
  const identifiers = [
    "name",
    "title",
    "label",
    "subject",
    "headline",
    "display_name",
    "displayName",
  ];
  const textFields = [
    "text",
    "message",
    "content",
    "description",
    "summary",
    "body",
  ];
  const idFields = ["id", "ID", "_id", "uuid", "uri", "url"];

  let primary = null;
  let secondary = null;

  // Find primary identifier
  for (const field of identifiers) {
    if (obj[field] && typeof obj[field] === "string") {
      primary = truncateStr(obj[field], 60);
      break;
    }
  }

  // Find text content
  for (const field of textFields) {
    const val = obj[field];
    if (val && typeof val === "string" && val !== primary) {
      secondary = truncateStr(val, 40);
      break;
    }
  }

  // Fallback to ID
  if (!primary) {
    for (const field of idFields) {
      if (obj[field]) {
        primary = `${field}: ${truncateStr(String(obj[field]), 40)}`;
        break;
      }
    }
  }

  // Last fallback: show first few keys
  if (!primary) {
    const preview = keys.slice(0, 3).map((k) => {
      const v = obj[k];
      if (v === null || v === undefined) return `${k}: null`;
      if (typeof v === "string") return `${k}: "${truncateStr(v, 20)}"`;
      if (typeof v === "object")
        return `${k}: ${Array.isArray(v) ? `[${v.length}]` : "{...}"}`;
      return `${k}: ${v}`;
    });
    return `{${preview.join(", ")}${keys.length > 3 ? ", ..." : ""}}`;
  }

  return secondary ? `${primary} - ${secondary}` : primary;
};

/**
 * Format object - show key-value pairs
 */
const formatObject = (obj, maxLines) => {
  if (!obj || typeof obj !== "object") return String(obj);

  const keys = Object.keys(obj);
  if (keys.length === 0) return "Empty object";

  // Only skip truly useless internal fields (start with _)
  const skipKeys = new Set(["_raw", "_metadata", "__proto__"]);

  const lines = [];
  let shown = 0;

  for (const key of keys) {
    if (skipKeys.has(key) || key.startsWith("__")) continue;
    if (shown >= maxLines - 1) {
      lines.push(`... +${keys.length - shown} more fields`);
      break;
    }

    const value = obj[key];
    const formattedKey = formatKey(key);
    const formattedValue = formatValue(value);

    lines.push(`${formattedKey}: ${formattedValue}`);
    shown++;
  }

  return lines.join("\n") || "Empty object";
};

/**
 * Format key - convert snake_case/camelCase to readable
 */
const formatKey = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

/**
 * Format a single value for display
 */
const formatValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "string") {
    if (value.length === 0) return '""';
    return truncateStr(value, 100);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    if (value.length <= 3 && value.every((v) => typeof v !== "object")) {
      return `[${value.join(", ")}]`;
    }
    // Show what's in the array
    const first = value[0];
    if (typeof first === "object" && first !== null) {
      const hint = getObjectSummary(first);
      return `[${value.length} items] e.g. ${hint}`;
    }
    return `[${value.length} items]`;
  }
  if (typeof value === "object") {
    return getObjectSummary(value);
  }
  return truncateStr(String(value), 100);
};

/**
 * Format tool input/result as pretty JSON
 */
const formatToolDataJSON = (data, maxLength = 1000) => {
  if (!data) return "null";
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      const json = JSON.stringify(parsed, null, 2);
      return json.length > maxLength
        ? json.substring(0, maxLength) + "\n..."
        : json;
    } catch {
      return data.substring(0, maxLength);
    }
  }
  const json = JSON.stringify(data, null, 2);
  return json.length > maxLength
    ? json.substring(0, maxLength) + "\n..."
    : json;
};

/**
 * ContentBlockRenderer - Renders interleaved content blocks (Claude Code style)
 * Handles text blocks and expandable tool cards with request/response sections
 */
const ContentBlockRenderer = ({ blocks, formatMessage }) => {
  const [expandedTools, setExpandedTools] = useState({});
  const [viewModes, setViewModes] = useState({}); // 'human' or 'json' per tool

  const toggleTool = (toolId) => {
    setExpandedTools((prev) => ({
      ...prev,
      [toolId]: !prev[toolId],
    }));
  };

  const toggleViewMode = (toolId, e) => {
    e.stopPropagation(); // Don't toggle expansion
    setViewModes((prev) => ({
      ...prev,
      [toolId]: prev[toolId] === "json" ? "human" : "json",
    }));
  };

  if (!blocks || blocks.length === 0) return null;

  return (
    <div className="content-blocks">
      {blocks.map((block, index) => {
        if (block.type === "text") {
          return (
            <div
              key={`text-${index}`}
              className={`content-block-text ${block.isSummary ? "summary-block" : ""}`}
              dangerouslySetInnerHTML={{ __html: formatMessage(block.content) }}
            />
          );
        } else if (block.type === "tool") {
          const isExpanded = expandedTools[block.toolId];
          const viewMode = viewModes[block.toolId] || "human";
          const statusClass =
            block.status === "queued"
              ? "queued"
              : block.status === "running"
                ? "running"
                : block.status === "success"
                  ? "success"
                  : "failed";
          const statusIcon =
            block.status === "queued"
              ? "🔜"
              : block.status === "running"
                ? "⏳"
                : block.status === "success"
                  ? "✅"
                  : "❌";

          const hasInput = block.input && Object.keys(block.input).length > 0;
          const hasResult = block.result !== null && block.result !== undefined;

          return (
            <div
              key={block.toolId || `tool-${index}`}
              className={`content-block-tool ${statusClass}`}
            >
              <div
                className="tool-header"
                onClick={() =>
                  block.status !== "running" &&
                  block.status !== "queued" &&
                  toggleTool(block.toolId)
                }
              >
                <span className="tool-expand-icon">
                  {block.status === "queued" ? (
                    <span className="tool-queued-icon">⏸</span>
                  ) : block.status === "running" ? (
                    <span className="tool-spinner"></span>
                  ) : (
                    <span
                      className={`tool-caret ${isExpanded ? "expanded" : ""}`}
                    >
                      ▶
                    </span>
                  )}
                </span>
                <span
                  className="tool-name"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(block.name);
                    const el = e.target;
                    el.classList.add("copied");
                    setTimeout(() => el.classList.remove("copied"), 1000);
                  }}
                  title="Click to copy tool name"
                >
                  {block.name}
                </span>
                <span className="tool-status-icon">{statusIcon}</span>
              </div>

              {/* Expanded tool details with Request/Response sections */}
              {isExpanded && (
                <div className="tool-details">
                  {/* View mode toggle */}
                  <div className="tool-view-toggle">
                    <button
                      className={`view-toggle-btn ${viewMode === "human" ? "active" : ""}`}
                      onClick={(e) => toggleViewMode(block.toolId, e)}
                    >
                      {viewMode === "human" ? "📖 Human" : "{ } JSON"}
                    </button>
                  </div>

                  {/* Request section */}
                  {hasInput && (
                    <div className="tool-section tool-request">
                      <div className="tool-section-header">📤 Request</div>
                      <pre className="tool-section-content">
                        {viewMode === "human"
                          ? formatToolDataHuman(block.input)
                          : formatToolDataJSON(block.input)}
                      </pre>
                    </div>
                  )}

                  {/* Response section */}
                  {hasResult && (
                    <div className="tool-section tool-response">
                      <div className="tool-section-header">📥 Response</div>
                      <pre className="tool-section-content">
                        {viewMode === "human"
                          ? formatToolDataHuman(block.result)
                          : formatToolDataJSON(block.result)}
                      </pre>
                    </div>
                  )}

                  {/* No data message */}
                  {!hasInput && !hasResult && (
                    <div className="tool-no-data">
                      No request/response data available
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const CopyButton = ({ content }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    // Strip HTML and convert to plain text for copying
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const plainText = tempDiv.textContent || tempDiv.innerText;

    navigator.clipboard.writeText(plainText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button className="copy-button" onClick={handleCopy} title="Copy message">
      {copied ? "✓ Copied" : "📋 Copy"}
    </button>
  );
};

const ChatInterface = ({
  messages,
  onSendMessage,
  isLoading,
  ghlConnected,
  isStreaming,
  onStopStreaming,
  agentStatus,
  progressMessages,
  harryZapCount = 0,
  isHarryActive = false,
  streamingMessage = null, // Progressive text streaming (like Claude Code)
  activeTools = [], // Currently running tools (streaming mode)
  completedTools = [], // Finished tools for this turn (streaming mode)
  messageQueueingEnabled = false, // MESSAGE_QUEUEING: Allow sending while loading
  queuedMessages = [], // MESSAGE_QUEUEING: Currently queued messages
  insertText = null, // Text to insert into input (from FileBrowser "Use in Chat")
  onInsertTextHandled = null, // Callback when insertText is processed
  commandCenterEnabled = false,
}) => {
  const [input, setInput] = useState("");
  // Slash-command skill menu ("/" at the start of the composer → pick a skill to run this turn)
  const [slashIndex, setSlashIndex] = useState(0);
  const [slashMatches, setSlashMatches] = useState([]);
  const slashOpen = slashQuery(input) !== null;
  // Fixed-position nudges (the "activate Ollie" toast in App.js) sit exactly
  // where the "/" menu drops down. Flag the body so CSS hides them while the
  // menu is open; cleared on unmount.
  useEffect(() => {
    try { document.body.classList.toggle("slash-menu-open", slashOpen); } catch (_) { /* SSR/tests */ }
    return () => { try { document.body.classList.remove("slash-menu-open"); } catch (_) { /* noop */ } };
  }, [slashOpen]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Image upload state
  const imageUploadEnabled = useFeatureFlag(FLAGS.IMAGE_UPLOAD);
  const [attachedFiles, setAttachedFiles] = useState([]); // Array of base64 file objects
  const [isDragging, setIsDragging] = useState(false);
  const [composerError, setComposerError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dragCounterRef = useRef(0); // Track nested drag events

  // Responsive state
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle insertText prop (from FileBrowser "Use in Chat")
  const textareaRef = useRef(null);
  const attachInputRef = useRef(null); // hidden file input for the paperclip picker
  useEffect(() => {
    if (insertText) {
      // Append to existing input or set if empty
      setInput(prev => prev ? `${prev}\n${insertText}` : insertText);
      // Focus the textarea
      setTimeout(() => textareaRef.current?.focus(), 100);
      // Notify parent that we handled it
      onInsertTextHandled?.();
    }
  }, [insertText, onInsertTextHandled]);

  // ═══════════════════════════════════════════════════════════════════════
  // STICKY-BOTTOM AUTO-SCROLL
  // ═══════════════════════════════════════════════════════════════════════
  //
  // The old implementation measured "am I near the bottom?" INSIDE the effect
  // that runs AFTER React has painted the new message. By then the new
  // content has already grown scrollHeight, so for any answer taller than the
  // 100px threshold — i.e. every tool-call result — the measurement said "the
  // user is 900px from the bottom, don't yank them" and the pane never
  // scrolled. The user was at the bottom; the content moved the bottom.
  // Result: new messages and tool results render below the fold and are never
  // seen. (Observed filming the Google OAuth demo: from shot 6 on, every
  // result was off-screen — docs/google-oauth/DEMO-VIDEO.md, 9th pass.)
  //
  // The fix is to record stickiness from the USER's scrolling, not from a
  // post-render measurement:
  //   • isPinnedToBottomRef holds the last known user intent.
  //   • The scroll listener is the ONLY thing that clears it, and it only
  //     runs on real scroll events.
  //   • New content scrolls iff the ref says pinned — so reading history is
  //     never interrupted, and sitting at the bottom always follows along.
  //
  // Content-driven scrolls are INSTANT, not smooth, deliberately: a smooth
  // scroll is in flight for ~300ms, firing scroll events that sample a
  // not-yet-at-bottom position and would clear the pin mid-animation —
  // unpinning the user without them touching anything. Instant lands in one
  // event, at the bottom, and the pin survives. Smooth is kept for the
  // explicit button press, where there is no follow-up content to race.
  const NEAR_BOTTOM_PX = 100;
  const isPinnedToBottomRef = useRef(true);

  const distanceFromBottom = (el) =>
    el.scrollHeight - el.scrollTop - el.clientHeight;

  const pinToBottom = useCallback((behavior = "instant") => {
    isPinnedToBottomRef.current = true;
    setShowScrollButton(false);
    const container = messagesContainerRef.current;
    // Set scrollTop directly as well as calling scrollIntoView: jsdom and
    // some embedded webviews no-op on scrollIntoView, and this is the only
    // thing standing between a user and content they cannot see.
    if (container) container.scrollTop = container.scrollHeight;
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  }, []);

  // Explicit user request (the scroll-to-bottom button) — re-pins.
  const scrollToBottom = useCallback(() => pinToBottom("smooth"), [pinToBottom]);

  // Conversation-switch scroll fix (Ashley 2026-09-01: "convo starts back at
  // the very top of the thread versus the last message"). A fresh thread
  // always opens at the newest message, and always re-pins.
  const prevThreadKeyRef = useRef(null);
  useEffect(() => {
    if (!messages?.length) return;
    const first = messages[0];
    const threadKey = first?.id || first?.timestamp || `${first?.role}:${String(first?.content).slice(0, 40)}`;
    if (prevThreadKeyRef.current !== threadKey) {
      prevThreadKeyRef.current = threadKey;
      requestAnimationFrame(() => pinToBottom("instant"));
    }
  }, [messages, pinToBottom]);

  // Follow new content only while pinned. Depends on lengths rather than the
  // array identities so a parent re-render with fresh arrays doesn't thrash.
  useEffect(() => {
    if (!isPinnedToBottomRef.current) return;
    const raf = requestAnimationFrame(() => pinToBottom("instant"));
    return () => cancelAnimationFrame(raf);
  }, [
    messages,
    streamingMessage,
    activeTools?.length,
    completedTools?.length,
    progressMessages?.length,
    isLoading,
    pinToBottom,
  ]);

  // The user's own scrolling is the single source of truth for the pin.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const atBottom = distanceFromBottom(container) < NEAR_BOTTOM_PX;
      isPinnedToBottomRef.current = atBottom;
      setShowScrollButton(!atBottom);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // IMAGE UPLOAD: Drag & Drop Handlers
  // ═══════════════════════════════════════════════════════════════════

  const handleDragEnter = useCallback(
    (e) => {
      if (!imageUploadEnabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current++;

      // Check if dragging files (not text selection)
      if (e.dataTransfer?.types?.includes("Files")) {
        setIsDragging(true);
      }
    },
    [imageUploadEnabled],
  );

  const handleDragLeave = useCallback(
    (e) => {
      if (!imageUploadEnabled) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current--;

      // Only hide overlay when truly leaving the container
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    },
    [imageUploadEnabled],
  );

  const handleDragOver = useCallback(
    (e) => {
      if (!imageUploadEnabled) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [imageUploadEnabled],
  );

  const processFiles = useCallback(async (files) => {
    const validFiles = [];
    const errors = [];

    const textAttachments = [];
    for (const file of files) {
      // Text-family (CSV/TSV/TXT/MD/JSON): read as text, attach as a text
      // chip — folded into the message on send.
      if (isTextFile(file)) {
        if (file.size > MAX_TEXT_FILE_BYTES) {
          errors.push(`${file.name}: Too large for inline text (max ${Math.round(MAX_TEXT_FILE_BYTES / 1024)}KB) — try splitting it`);
          continue;
        }
        try {
          const text = await file.text();
          textAttachments.push({ type: "text", name: file.name, size: file.size, text });
        } catch (_) {
          errors.push(`${file.name}: Couldn't read the file`);
        }
        continue;
      }
      // Audio/video → Files upload + auto-transcription (not base64 chat).
      if (isAudioVideoFile(file)) {
        if (file.size / (1024 * 1024) > MAX_AV_FILE_MB) {
          errors.push(`${file.name}: Too large (max ${MAX_AV_FILE_MB}MB for recordings)`);
          continue;
        }
        try {
          const { authFetch, apiUrl: buildUrl } = await import("../utils/api");
          const formData = new FormData();
          formData.append("file", file);
          const res = await authFetch(buildUrl("/api/c3/files/upload"), {
            method: "POST",
            body: formData,
          });
          if (!res.ok) throw new Error(`upload failed (${res.status})`);
          const uploaded = await res.json().catch(() => ({}));
          const finalName = uploaded?.filename || file.name;
          // Ride the normal message path as a text attachment so the agent
          // KNOWS the recording exists and picks up the thread when the
          // transcript lands.
          textAttachments.push({
            type: "text",
            name: file.name,
            size: file.size,
            text: `[RECORDING UPLOADED: "${finalName}" is in my Files and transcribing now — the transcript will appear as "${finalName}.transcript.txt". Read it with your file tools when it's ready and continue the analysis.]`,
          });
        } catch (err) {
          console.error("[AVUpload] failed:", err);
          errors.push(`${file.name}: Upload failed — try again or use the Files area`);
        }
        continue;
      }
      // Check file type
      if (!ALL_SUPPORTED_TYPES.includes(file.type)) {
        errors.push(
          `${file.name}: Unsupported type (use PNG, JPG, GIF, WebP, PDF, CSV, TXT, MD, JSON — or drop audio/video and I'll transcribe it)`,
        );
        continue;
      }

      // Check file size
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > MAX_FILE_SIZE_MB) {
        errors.push(
          `${file.name}: Too large to attach inline (max ${MAX_FILE_SIZE_MB}MB for images and PDFs). Upload it in Files and ask me to read it from there.`,
        );
        continue;
      }

      validFiles.push(file);
    }

    // Show errors if any
    if (errors.length > 0) {
      console.warn("[ImageUpload] Rejected files:", errors);
      setComposerError(errors.join(" "));
    } else {
      setComposerError("");
    }

    if (textAttachments.length > 0) {
      setAttachedFiles((prev) => [...prev, ...textAttachments]);
    }

    // Convert valid files to base64
    if (validFiles.length > 0) {
      try {
        const converted = await Promise.all(validFiles.map(fileToBase64));
        setAttachedFiles((prev) => [...prev, ...converted]);
        console.log(`[ImageUpload] Added ${converted.length} files`);
      } catch (error) {
        console.error("[ImageUpload] Failed to convert files:", error);
        setComposerError("Those files could not be attached. Please try again.");
      }
    }
  }, []);

  const handleDrop = useCallback(
    async (e) => {
      if (!imageUploadEnabled) return;
      e.preventDefault();
      e.stopPropagation();

      dragCounterRef.current = 0;
      setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length > 0) {
        await processFiles(files);
      }
    },
    [imageUploadEnabled, processFiles],
  );

  // Handle paste (Cmd+V / Ctrl+V) for screenshots
  const handlePaste = useCallback(
    async (e) => {
      if (!imageUploadEnabled) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      // Excel/Sheets/Numbers put BOTH a text flavor AND a bitmap rendition on
      // the clipboard for cell-range copies. Grabbing the file item first
      // swallowed big data pastes as IMAGES ("it started becoming an image
      // every single time" — Melissa call item #9). If the clipboard carries
      // substantial text, that's what the user meant to paste — let the
      // normal text paste proceed. Short/absent text (screenshots, copied
      // images whose text flavor is just a URL) still attach as files.
      const pastedText = e.clipboardData.getData("text/plain") || "";
      if (pastedText.trim().length >= 50) return;

      const files = [];
      for (const item of items) {
        if (item.kind === "file" && ALL_SUPPORTED_TYPES.includes(item.type)) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        e.preventDefault(); // Prevent default paste behavior for images
        await processFiles(files);
      }
      // If no files, let normal text paste happen
    },
    [imageUploadEnabled, processFiles],
  );

  // Remove an attached file
  const removeAttachedFile = useCallback((index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Format tool block into terse summary for copy/paste
  const formatToolForCopy = (block) => {
    const status =
      block.status === "success" ? "✓" : block.status === "failed" ? "✗" : "…";
    const name = block.name || "unknown_tool";

    // Terse request summary
    let request = "";
    if (block.input && Object.keys(block.input).length > 0) {
      const keys = Object.keys(block.input).slice(0, 3);
      request = keys
        .map((k) => {
          const v = block.input[k];
          const val =
            typeof v === "string"
              ? v.substring(0, 30)
              : JSON.stringify(v).substring(0, 30);
          return `${k}: ${val}`;
        })
        .join(", ");
      if (Object.keys(block.input).length > 3) request += "...";
    }

    // Terse response summary
    let response = "";
    if (block.result) {
      if (typeof block.result === "string") {
        response = block.result.substring(0, 50).replace(/\n/g, " ");
      } else if (block.result.content) {
        response = String(block.result.content)
          .substring(0, 50)
          .replace(/\n/g, " ");
      } else {
        response = JSON.stringify(block.result).substring(0, 50);
      }
      if (response.length >= 50) response += "...";
    }

    return `[${status}] ${name}${request ? ` | ${request}` : ""}${response ? ` → ${response}` : ""}`;
  };

  const copyToClipboard = async (message, index) => {
    try {
      let copyText = "";

      // If message has contentBlocks, build structured output preserving interleaved order
      if (message.contentBlocks && message.contentBlocks.length > 0) {
        // Iterate through blocks IN ORDER to preserve interleaving
        message.contentBlocks.forEach((block) => {
          if (block.type === "tool") {
            copyText += formatToolForCopy(block) + "\n\n";
          } else if (block.type === "text" && block.content) {
            const formattedHtml = formatMessage(block.content);
            copyText += htmlToGoogleDocsText(formattedHtml) + "\n\n";
          }
        });
        // Trim trailing newlines
        copyText = copyText.trim();
      } else {
        // Legacy: just content string
        const content = typeof message === "string" ? message : message.content;
        const formattedHtml = formatMessage(content);
        copyText = htmlToGoogleDocsText(formattedHtml);
      }

      await navigator.clipboard.writeText(copyText);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      // Fallback to basic text copy
      const content = typeof message === "string" ? message : message.content;
      const plainText = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");
      navigator.clipboard.writeText(plainText);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }
  };

  // Utility to convert HTML to clean Google Docs-friendly text
  const htmlToGoogleDocsText = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Process tables
    const tables = tempDiv.querySelectorAll("table");
    tables.forEach((table) => {
      let tableText = "\n";
      const rows = table.querySelectorAll("tr");
      rows.forEach((row, i) => {
        const cells = row.querySelectorAll("th, td");
        const cellTexts = Array.from(cells).map((cell) =>
          cell.textContent.trim(),
        );
        tableText += cellTexts.join("\t") + "\n";
        if (i === 0 && row.querySelectorAll("th").length > 0) {
          tableText += cellTexts.map(() => "---").join("\t") + "\n";
        }
      });
      table.replaceWith(document.createTextNode(tableText));
    });

    // Process code blocks
    const codeBlocks = tempDiv.querySelectorAll("pre, code");
    codeBlocks.forEach((block) => {
      const code = block.textContent;
      block.replaceWith(document.createTextNode("\n" + code + "\n"));
    });

    // Process headers - add line breaks and emphasis
    tempDiv.querySelectorAll("h1").forEach((h) => {
      h.replaceWith(
        document.createTextNode("\n\n" + h.textContent.toUpperCase() + "\n"),
      );
    });
    tempDiv.querySelectorAll("h2").forEach((h) => {
      h.replaceWith(document.createTextNode("\n\n" + h.textContent + "\n"));
    });
    tempDiv.querySelectorAll("h3").forEach((h) => {
      h.replaceWith(document.createTextNode("\n" + h.textContent + "\n"));
    });

    // Process lists
    tempDiv.querySelectorAll("li").forEach((li) => {
      li.replaceWith(document.createTextNode("\n• " + li.textContent));
    });

    // Process line breaks
    tempDiv.querySelectorAll("br").forEach((br) => {
      br.replaceWith(document.createTextNode("\n"));
    });

    // Process horizontal rules
    tempDiv.querySelectorAll("hr").forEach((hr) => {
      hr.replaceWith(document.createTextNode("\n---\n"));
    });

    // Get clean text
    let text = tempDiv.textContent || tempDiv.innerText || "";

    // Clean up excessive line breaks
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text.trim();

    return text;
  };

  // Copy individual block (code, table, etc)
  const copyBlock = async (element, index) => {
    try {
      const content = element.textContent || element.innerText;
      const cleanText = htmlToGoogleDocsText(element.innerHTML);

      await navigator.clipboard.writeText(cleanText);
      setCopiedIndex(`block-${index}`);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const escapeHtml = (str) => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const formatMessage = (content) => {
    // Handle undefined or null content
    if (!content) return "";

    // Harry completion messages carry a machine marker (c3Service: <!--harry:taskId-->)
    // that App.js uses to spot them; it is not for humans — drop it before escaping,
    // otherwise it renders as literal text at the top of the reply (seen 2026-09-15).
    const cleaned = String(content).replace(/^\s*<!--harry:[^>]*-->\s*/, "");
    // Escape raw HTML to prevent XSS — must happen before markdown-to-HTML conversion
    // Markdown formatting (bold, tables, code blocks, headers) still works because
    // those regexes generate safe HTML on top of the escaped content
    let formatted = escapeHtml(cleaned);
    let blockIndex = 0;

    // Convert markdown tables to HTML with copy button
    formatted = formatted.replace(
      /\|(.+)\|[\r\n]+\|[-:\s|]+\|[\r\n]+((?:\|.+\|[\r\n]*)+)/g,
      (match, header, rows) => {
        const headers = header.split("|").filter((h) => h.trim());
        const rowsArray = rows
          .trim()
          .split("\n")
          .map((row) =>
            row
              .split("|")
              .filter((cell) => cell.trim())
              .map((cell) => cell.trim()),
          );

        const tableId = `table-${blockIndex++}`;
        return `<div class="copyable-block-wrapper">
          <button class="block-copy-btn" data-block-id="${tableId}" onclick="window.copyTableBlock('${tableId}')">📋</button>
          <table class="message-table" id="${tableId}">
            <thead><tr>${headers.map((h) => `<th>${h.trim()}</th>`).join("")}</tr></thead>
            <tbody>${rowsArray.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("")}</tbody>
          </table>
        </div>`;
      },
    );

    // Convert headers (must be before bold to avoid conflicts)
    formatted = formatted.replace(/^### (.+)$/gm, "<h3>$1</h3>");
    formatted = formatted.replace(/^## (.+)$/gm, "<h2>$1</h2>");
    formatted = formatted.replace(/^# (.+)$/gm, "<h1>$1</h1>");

    // Convert code blocks with copy button
    formatted = formatted.replace(
      /```(\w*)\n?([\s\S]*?)```/g,
      (match, lang, code) => {
        const codeId = `code-${blockIndex++}`;
        const trimmedCode = code.trim();
        return `<div class="copyable-block-wrapper">
          <button class="block-copy-btn" data-block-id="${codeId}" onclick="window.copyCodeBlock('${codeId}')">📋</button>
          <pre id="${codeId}"><code>${trimmedCode}</code></pre>
        </div>`;
      },
    );
    formatted = formatted.replace(/`([^`]+)`/g, "<code>$1</code>");

    // Convert bold and italic
    formatted = formatted.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    formatted = formatted.replace(/\*([^*]+)\*/g, "<em>$1</em>");

    // Convert lists
    formatted = formatted.replace(/^- (.+)$/gm, "<li>$1</li>");
    formatted = formatted.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

    // Convert horizontal rules
    formatted = formatted.replace(/^---$/gm, "<hr>");

    // Convert URLs to clickable links (before line breaks)
    // Match URLs that aren't already inside href or src attributes
    formatted = formatted.replace(
      /(?<!href="|src="|">)(https?:\/\/[^\s<>"')]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
    );

    // Convert line breaks (last to preserve structure)
    formatted = formatted.replace(/\n/g, "<br>");

    return formatted;
  };

  // Set up global copy functions for onclick handlers
  React.useEffect(() => {
    window.copyTableBlock = async (tableId) => {
      const table = document.getElementById(tableId);
      if (!table) return;

      const cleanText = htmlToGoogleDocsText(table.outerHTML);
      try {
        await navigator.clipboard.writeText(cleanText);
        const btn = document.querySelector(`[data-block-id="${tableId}"]`);
        if (btn) {
          btn.textContent = "✓";
          btn.classList.add("copied");
          setTimeout(() => {
            btn.textContent = "📋";
            btn.classList.remove("copied");
          }, 2000);
        }
      } catch (err) {
        console.error("Failed to copy table:", err);
      }
    };

    window.copyCodeBlock = async (codeId) => {
      const code = document.getElementById(codeId);
      if (!code) return;

      const cleanText = code.textContent || code.innerText;
      try {
        await navigator.clipboard.writeText(cleanText);
        const btn = document.querySelector(`[data-block-id="${codeId}"]`);
        if (btn) {
          btn.textContent = "✓";
          btn.classList.add("copied");
          setTimeout(() => {
            btn.textContent = "📋";
            btn.classList.remove("copied");
          }, 2000);
        }
      } catch (err) {
        console.error("Failed to copy code:", err);
      }
    };

    return () => {
      delete window.copyTableBlock;
      delete window.copyCodeBlock;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasText = input.trim();
    const hasFiles = attachedFiles.length > 0;

    // Allow submit when: not loading OR message queueing is enabled (can queue while loading).
    // isSubmitting must NOT block queueing: the primary send stays "submitting" for its
    // entire stream, which used to silently kill steering (multiple messages in a row).
    const canQueueNow = messageQueueingEnabled && isStreaming;
    if ((hasText || hasFiles) && (!isLoading || messageQueueingEnabled) && (!isSubmitting || canQueueNow)) {
      const submittedInput = input;
      const submittedFiles = attachedFiles;
      setComposerError("");
      setIsSubmitting(true);
      // Optimistic clear: empty the composer the moment the command is
      // submitted (waiting for the full stream made drafts linger for the
      // entire response). Restored below if the send actually fails.
      setInput((current) => (current === submittedInput ? "" : current));
      setAttachedFiles((current) => (current === submittedFiles ? [] : current));
      let accepted = false;
      let interrupted = false;
      // Build message content - can be string or structured with attachments.
      // Text-family files fold into the message body as fenced blocks; only
      // binary files (image/PDF) travel as structured attachments.
      try {
        const textFiles = submittedFiles.filter((f) => f.type === "text");
        const binaryFiles = submittedFiles.filter((f) => f.type !== "text");
        const foldedText = [
          ...textFiles.map((f) => `Contents of uploaded file "${f.name}":\n\u0060\u0060\u0060\n${f.text}\n\u0060\u0060\u0060`),
          submittedInput.trim(),
        ].filter(Boolean).join("\n\n");
        const result = binaryFiles.length > 0 && imageUploadEnabled
          ? await onSendMessage({
            text: foldedText,
            attachments: binaryFiles,
          })
          : await onSendMessage(foldedText);
        // "interrupted" = the message was saved but the reply was cut off
        // (deploy restart, dropped stream). Restore the draft for a resend,
        // but never tell the user it was not sent — it was.
        interrupted = result === "interrupted";
        accepted = result !== false && !interrupted;
      } catch (error) {
        accepted = false;
      } finally {
        setIsSubmitting(false);
      }
      if (!accepted) {
        // Restore the draft only if the user hasn't started typing a new one
        setInput((current) => (current === "" ? submittedInput : current));
        setAttachedFiles((current) => (current.length === 0 ? submittedFiles : current));
        setComposerError(
          interrupted
            ? "Your message was saved, but the reply was cut off before it finished (usually a restart on our side). It's back in the box — send it again in a minute."
            : "Your command was not sent. Your draft is still here—check your connection and try again.",
        );
      }
    }
  };

  const pickSlashSkill = (s) => {
    setInput(`/${s.slug} `);
    setSlashIndex(0);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleKeyDown = (e) => {
    // Slash menu navigation: ↑/↓ move, Tab/Enter pick, Esc closes (keeps the text)
    if (slashOpen && slashMatches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSlashIndex((i) => (i + 1) % slashMatches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length); return; }
      if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) { e.preventDefault(); pickSlashSkill(slashMatches[slashIndex] || slashMatches[0]); return; }
      if (e.key === "Escape") { setSlashMatches([]); return; }
    }
    // Submit on Enter, but allow Shift+Enter for new lines
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleVoiceTranscription = (transcription) => {
    if (transcription && transcription.trim()) {
      // Append transcription to existing input (in case user was typing)
      const newInput = input ? `${input}\n${transcription}` : transcription;
      setInput(newInput);
    }
  };

  return (
    <div
      className={`chat-interface ${isDragging ? "drag-active" : ""}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && imageUploadEnabled && (
        <div className="drag-overlay">
          <div className="drag-overlay-content">
            <span className="drag-icon">📎</span>
            <span className="drag-text">Drop images or PDFs here</span>
          </div>
        </div>
      )}

      <div className="messages-container" ref={messagesContainerRef}>
        {messages.length === 0 && (
          <div className="welcome-message hf-reveal">
            <h3>
              {commandCenterEnabled
                ? "What should we accomplish?"
                : "🦦 Hey! I'm Ollie, your AI Operations Officer."}
            </h3>
            <p>
              {commandCenterEnabled
                ? "Give Ollie a business outcome. The plan, execution, and results will stay together in this command workspace."
                : "I connect your tools and execute real workflows — no copy-paste, no tab-switching, just results."}
            </p>
            <p
              style={{
                fontWeight: "600",
                marginTop: "16px",
                color: "var(--text-primary)",
              }}
            >
              {commandCenterEnabled ? "Try a command" : "🚀 Try one of these:"}
            </p>
            <ul className="hf-stagger">
              {[
                "Find 10 SaaS founders in Austin with 11-50 employees",
                "Ask the coach: my offer isn't converting, what's the one thing to fix?",
                "Ask Harry to write me a research report",
              ].map((suggestion) => (
                <li
                  key={suggestion}
                  className="hf-lift hf-press hf-focus"
                  role="button"
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                  onClick={() => setInput(suggestion)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setInput(suggestion);
                    }
                  }}
                >
                  "{suggestion}"
                </li>
              ))}
            </ul>
            <p
              style={{
                fontSize: "13px",
                color: "var(--text-tertiary)",
                marginTop: "16px",
              }}
            >
              {commandCenterEnabled
                ? "Connections determine which actions Ollie can complete for you."
                : "💡 Add more connections and tools in Settings → Integrations"}
            </p>
          </div>
        )}

        {messages.map((message, index) => (
          <React.Fragment key={index}>
            <div className={`message ${message.role} hf-reveal`}>
              <div className="message-content">
                {/* Use ContentBlockRenderer if message has contentBlocks (streaming mode) */}
                {message.contentBlocks && message.contentBlocks.length > 0 ? (
                  <ContentBlockRenderer
                    blocks={message.contentBlocks}
                    formatMessage={formatMessage}
                  />
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: formatMessage(message.content),
                    }}
                  />
                )}
              </div>
            </div>
            {message.role === "assistant" && (
              <div className="message-meta hf-reveal">
                {message.timestamp && (
                  <span
                    className="timestamp-badge"
                    title={new Date(message.timestamp).toLocaleString()}
                  >
                    {new Date(message.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
                {message.latency && (
                  <span className="latency-badge" title="Time to complete">
                    {parseFloat(message.latency).toFixed(1)}s
                  </span>
                )}
                <button
                  className={`copy-button ${copiedIndex === index ? "hf-reveal-scale hf-glow-success" : ""}`}
                  onClick={() => copyToClipboard(message, index)}
                  title="Copy message"
                >
                  {copiedIndex === index ? "✓ Copied" : "📋"}
                </button>
              </div>
            )}
            {message.role === "user" && (
              <div className="message-meta hf-reveal">
                <button
                  className={`copy-button ${copiedIndex === index ? "hf-reveal-scale hf-glow-success" : ""}`}
                  onClick={() => copyToClipboard(message, index)}
                  title="Copy message"
                >
                  {copiedIndex === index ? "✓ Copied" : "📋"}
                </button>
              </div>
            )}
            {/* Only show "tools used" line when NOT using contentBlocks (tools already shown inline) */}
            {message.toolsUsed &&
              message.toolsUsed.length > 0 &&
              !message.contentBlocks && (
                <div className="tools-used">
                  🔧 Used: {message.toolsUsed.join(", ")}
                </div>
              )}
          </React.Fragment>
        ))}

        {/* Streaming message with contentBlocks (Claude Code style) */}
        {streamingMessage && streamingMessage.contentBlocks && (
          <div className="message assistant streaming-message hf-reveal">
            <div className="message-content">
              <ContentBlockRenderer
                blocks={streamingMessage.contentBlocks}
                formatMessage={formatMessage}
              />
              {streamingMessage.isStreaming && (
                <span className="streaming-cursor">▊</span>
              )}
            </div>
          </div>
        )}

        {/* Legacy streaming (text only, no contentBlocks) */}
        {streamingMessage &&
          !streamingMessage.contentBlocks &&
          streamingMessage.content && (
            <div className="message assistant streaming-message hf-reveal">
              <div
                className="message-content"
                dangerouslySetInnerHTML={{
                  __html:
                    formatMessage(streamingMessage.content) +
                    (streamingMessage.isStreaming
                      ? '<span class="streaming-cursor">▊</span>'
                      : ""),
                }}
              />
            </div>
          )}

        {/* Legacy inline tool indicators (when not using contentBlocks) */}
        {!streamingMessage?.contentBlocks &&
          (activeTools.length > 0 || completedTools.length > 0) && (
            <div className="streaming-tools-container">
              {completedTools.map((tool) => (
                <div
                  key={tool.toolId}
                  className={`streaming-tool-indicator ${tool.success ? "success" : "failed"}`}
                >
                  <span>{tool.success ? "✅" : "❌"}</span>
                  <span>{tool.name}</span>
                </div>
              ))}
              {activeTools.map((tool) => (
                <div
                  key={tool.toolId}
                  className="streaming-tool-indicator running"
                >
                  <div className="streaming-tool-spinner"></div>
                  <span>{tool.name}</span>
                </div>
              ))}
            </div>
          )}

        {agentStatus && (
          <div className="agent-status-message atoms-status">
            <span className="status-text">{agentStatus}</span>
          </div>
        )}

        {isLoading && !agentStatus && (
          <div className="message assistant message-content loading hf-reveal">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="scroll-to-bottom-button"
          aria-label="Scroll to bottom"
        >
          ↓
        </button>
      )}

      {/* The nudge sits directly above the composer — hide it while the "/"
          skill menu is open so the menu's rows never render underneath it. */}
      {!slashOpen && <InstallNudge messageCount={messages?.length || 0} />}
      <form onSubmit={handleSubmit} className="input-form">
        {composerError && (
          <div className="composer-error" role="alert">
            {composerError}
          </div>
        )}
        {/* Attached files preview */}
        {attachedFiles.length > 0 && imageUploadEnabled && (
          <div className="attached-files-preview">
            {attachedFiles.map((file, index) => (
              <div key={index} className="attached-file">
                {file.previewUrl ? (
                  <img
                    src={file.previewUrl}
                    alt={file.name}
                    className="attached-file-thumbnail"
                  />
                ) : (
                  <div className="attached-file-icon">📄</div>
                )}
                <span className="attached-file-name" title={file.name}>
                  {file.name.length > 15
                    ? file.name.substring(0, 12) + "..."
                    : file.name}
                </span>
                <button
                  type="button"
                  className="attached-file-remove"
                  onClick={() => removeAttachedFile(index)}
                  aria-label="Remove file"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Visible attach affordance — drag-drop and paste always worked, but
            nothing on screen said so (Ashley: "Can I upload files to OS?"
            was answered with GitHub setup). Same processFiles path as drop. */}
        {imageUploadEnabled && (
          <>
            <input
              ref={attachInputRef}
              type="file"
              multiple
              accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.csv,.tsv,.txt,.md,.json,audio/*,video/*"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = ""; // reset so the same file can be re-picked
                if (files.length > 0) await processFiles(files);
              }}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className="chat-action-btn attach-button"
              onClick={() => attachInputRef.current?.click()}
              disabled={isSubmitting}
              aria-label="Attach files"
              title="Attach files (PNG, JPG, PDF, CSV, TXT, MD, JSON)"
            >
              <Paperclip size={18} />
            </button>
          </>
        )}
        {slashOpen && (
          <SlashSkillMenu query={slashQuery(input)} activeIndex={slashIndex} onPick={pickSlashSkill} onMatches={(m) => { setSlashMatches(m); setSlashIndex(0); }} />
        )}
        <TextareaAutosize
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={
            attachedFiles.length > 0
              ? "Add a message about these files..."
              : isMobile
                ? "Ask me anything"
                : "Ask me anything about your business..."
          }
          disabled={isSubmitting || ((isLoading || isStreaming) && !messageQueueingEnabled)}
          className="message-input"
          minRows={1}
          maxRows={6}
        />
        <VoiceInputButton
          onTranscriptionComplete={handleVoiceTranscription}
          disabled={isSubmitting || ((isLoading || isStreaming) && !messageQueueingEnabled)}
        />
        <VoiceReplyToggle />

        {isStreaming && (
          <button
            type="button"
            className="stop-button"
            onClick={onStopStreaming}
            style={{
              backgroundColor: "var(--accent-danger)",
              color: "var(--text-on-accent)",
              border: "none",
              borderRadius: "8px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {isHarryActive && harryZapCount > 0 ? "🐍 Stop" : "⏹ Stop"}
          </button>
        )}
        {/* Show send button when: not streaming, OR streaming with queueing enabled */}
        {(!isStreaming || messageQueueingEnabled) && (
          <button
            type="submit"
            disabled={
              (!input.trim() && attachedFiles.length === 0) ||
              isSubmitting ||
              ((isLoading || isStreaming) && !messageQueueingEnabled)
            }
            className={`chat-action-btn send-button ${
              (input.trim() || attachedFiles.length > 0) && !isSubmitting
                ? "is-active"
                : ""
            }`}
            aria-label={(isLoading || isStreaming) && messageQueueingEnabled ? "Queue command" : "Send command"}
          >
            {(isLoading || isStreaming) && messageQueueingEnabled ? (
              <Plus size={18} />
            ) : isLoading || isStreaming ? (
              <Loader2 size={18} className="spin" />
            ) : commandCenterEnabled ? (
              <ArrowUp size={18} />
            ) : (
              <Rocket size={18} />
            )}
          </button>
        )}
      </form>
    </div>
  );
};

export default ChatInterface;
